import Form from 'deltoid/core/Form';
import Button from 'deltoid/core/Button';
import CreatableSelect from '@components/BaseComponents/CreatableSelect';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { businessDetailsActions, businessDetailsSelectors, panDetailsSelectors, merchantSelectors, masterDataSelectors, masterDataActions } from '@store/ducks';
import { Row, Col } from 'deltoid/core/Blocks/BootstrapContainers';
import ErrorWrapper from '@components/BaseComponents/ErrorWrapper';
import FormFooter from '@components/BaseComponents/FormFooter';
import EXPECTED_SALES_OPTIONS from "@constants/profileConstants";
import { nameAlphaNumRegex, brandNameRegex } from "@utils/validations";
import useDebounce from "@hooks/useDebounce";

export const BusinessDetails = ({
  categories,
  fetchBusinessDetails,
  submitBusinessDetails,
  verifiedPanDetails,
  getMerchantBusinessDetails,
  businessEntities,
  fetchGstDetails,
  fetchShopDetails,
  getTradeOptions,
  editable,
  why,
  getAreaCodes,
  areaCodes: areaCodeOptions,
}) => {
  const { handleSubmit, control, getValues, setValue, errors, register, setError } = useForm({ mode: 'onBlur' });
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [gstInOptions, setGstInOptions] = useState([]);
  const [tradeNameOptions, setTradeNameOptions] = useState([]);
  const categoryOptions = categories
    ? categories.map((category) => ({
      label: category.name,
      value: category.name,
      id: category.id,
      business_sub_categories: category.business_sub_categories,
    }))
    : [];
  const { list } = businessEntities || { list: [] };
  const { name: entityType } = list.find(
    (entity) => getMerchantBusinessDetails.business_entity_id === entity.id
  ) || { name: null };
  const isSoleProprietorship = entityType === 'Sole Proprietorship';
  const [shopCode, setShopCode] = useState(null);
  const debouncedShopCode = useDebounce(shopCode, 500);

  useEffect(() => {
    if (debouncedShopCode !== null) {
      const { shopCode, areaCode } = getValues();
      getTradeName(shopCode, (areaCode && areaCode.value));
    }
  }, [debouncedShopCode]);

  useEffect(() => {
    fetchBusinessDetails();
    getAreaCodes();
  }, []);

  useEffect(() => {
    if (verifiedPanDetails && verifiedPanDetails.gstDetails) {
      setGstInOptions(
        verifiedPanDetails.gstDetails.map((v) => ({
          label: v.gstinId,
          value: v.gstinId,
        }))
      );
    }
  }, [verifiedPanDetails])

  useEffect(() => {
    if (getMerchantBusinessDetails) {
      const {
        business_category_id,
        business_sub_category_id,
        gst_number,
        monthly_expected_volume,
        business_name,
        pancard_name,
        business_pan_name_match,
        shop_number,
        area_code,
      } = getMerchantBusinessDetails;
      const category = categoryOptions.find(
        (v) => v.id === business_category_id
      );
      if (category) {
        onCategorySelected([category]);
        setValue("businessCategory", category);
        const subCategory = category.business_sub_categories
          ? category.business_sub_categories.find(
            (sub) => sub.id === business_sub_category_id
          )
          : null;
        if (subCategory)
          setValue("businessSubCategory", {
            label: subCategory.name,
            value: subCategory.name,
            id: subCategory.id,
          });
      }
      if (business_name) {
        const businessOption = { label: business_name, value: business_name, };
        setValue("tradeName", businessOption);
        setTradeNameOptions(
          business_pan_name_match ? [businessOption] : [{ label: pancard_name, value: pancard_name }, businessOption])
      } else {
        setTradeNameOptions([{ label: pancard_name, value: pancard_name }])
      }
      if (gst_number) {
        const gstOption = { label: gst_number, value: gst_number, };
        setValue("gstIn", gstOption);
        setGstInOptions([gstOption])
      }
      if (shop_number) {
        setValue("shopCode", shop_number);
      }
      if (area_code) {
        setValue("areaCode", areaCodeOptions.find((v) => v.value == area_code));
      }
      if (monthly_expected_volume)
        setValue(
          "expectedSales",
          EXPECTED_SALES_OPTIONS.find((v) => v.value == monthly_expected_volume)
        );
    }
  }, [getMerchantBusinessDetails]);

  useEffect(() => {
    const { gstIn, shopCode, areaCode } = getValues();
    let tradeNameList = [];
    if (gstIn && getTradeOptions.gstIn[gstIn.value])
      !tradeNameOptions.find(v => v.value === getTradeOptions.gstIn[gstIn.value]) && (
        tradeNameList = tradeNameList.concat(getTradeOptions.gstIn[gstIn.value])
      );
    if (shopCode && areaCode && getTradeOptions.shop[shopCode + areaCode.value])
      !tradeNameOptions.find(v => v.value === getTradeOptions.shop[shopCode + areaCode.value]) && (
        tradeNameList = tradeNameList.concat(getTradeOptions.shop[shopCode + areaCode.value])
      );
    let dynamicOptions = tradeNameList.map(v => ({ label: v, value: v }));
    setTradeNameOptions([...tradeNameOptions, ...dynamicOptions]);
  }, [getTradeOptions])

  const onCategorySelected = ([inputValue]) => {
    setValue("businessSubCategory", null);
    setSubCategoryOptions(
      categories
        .filter((category) => category.id === inputValue.id)[0]
        .business_sub_categories.map((subCategory) => ({
          label: subCategory.name,
          value: subCategory.name,
          id: subCategory.id,
        }))
    );
    return inputValue;
  };

  const onGstInCreate = (inputValue) => {
    if (
      !inputValue ||
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        inputValue.toUpperCase()
      )
    ) {
      setGstInOptions([
        { label: inputValue, value: inputValue },
        ...gstInOptions,
      ]);
      setValue(
        "gstIn",
        { label: inputValue, value: inputValue },
        { shouldValidate: true }
      );
      if (isSoleProprietorship) fetchGstDetails(inputValue);
    } else {
      setError('gstIn', 'regex', 'You seem to have entered an incorrect GSTIN number, kindly retry');
    }
  };

  const getTradeName = (shopCode, areaCode) => {
    if (isSoleProprietorship && !errors.shopCode && areaCode)
      fetchShopDetails(shopCode, areaCode);
  }

  const onSubmit = (data) => {
    if (editable) submitBusinessDetails(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row className='p-4'>
        <Col md={6}>
          <Form.Group>
            <Form.Label
              id="labelBusinessCategory"
            >
              Business Category
            </Form.Label>
            <Controller
              as={Form.Select}
              name="businessCategory"
              id="businessCategory"
              control={control}
              rules={{ required: "Please select a Business Category" }}
              placeholder="Select Business Category"
              options={categoryOptions}
              onChange={(inputValue) => onCategorySelected(inputValue)}
              isDisabled={!editable}
              isSearchable={true}
            />
            <ErrorWrapper errors={errors} fieldName='businessCategory' />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group>
            <Form.Label
              id="labelBusinessSubCategory"
            >
              Business Sub Category
            </Form.Label>
            <Controller
              as={Form.Select}
              control={control}
              id="businessSubCategory"
              name="businessSubCategory"
              placeholder="Select a sub-category"
              rules={{ required: "Please select a Business Sub-Category" }}
              options={subCategoryOptions}
              onChange={([inputValue]) => inputValue}
              value={getValues("businessSubCategory")}
              isDisabled={!editable}
              isSearchable={true}
            />
            <ErrorWrapper errors={errors} fieldName='businessSubCategory' />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label
              id="labelGstIn"
            >
              GSTIN (optional)
            </Form.Label>
            <Controller
              as={CreatableSelect}
              name="gstIn"
              id="gstIn"
              control={control}
              placeholder="Select or Enter GSTIN"
              options={gstInOptions}
              onCreateOption={onGstInCreate}
              value={getValues("gstIn")}
              onChange={([inputValue]) => {
                if (isSoleProprietorship) fetchGstDetails(inputValue.value);
                return inputValue;
              }}
              isDisabled={!editable}
            />
            <ErrorWrapper errors={errors} fieldName='gstIn' />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label
              id="labelExpectedSales"
            >
              Expected Sales per month
            </Form.Label>
            <Controller
              as={Form.Select}
              id="expectedSales"
              name="expectedSales"
              control={control}
              rules={{ required: "Please select Expected Sales Volume" }}
              placeholder="Select estimated sales"
              options={EXPECTED_SALES_OPTIONS}
              isDisabled={!editable}
              isSearchable={true}
            />
            <ErrorWrapper errors={errors} fieldName='expectedSales' />
          </Form.Group>
        </Col>
        {isSoleProprietorship && (
          <>
            <Col md={6}>
              <Form.Group>
                <Form.Label id='shopCode'>Shop code (optional)</Form.Label>
                <Form.Text
                  ref={register({
                    validate: { nameAlphaNumRegex }
                  })}
                  name='shopCode'
                  id='shopCode'
                  placeholder='Enter your shop code'
                  onChange={(e) => setShopCode(e.target.value)}
                  readOnly={!editable}
                />
                <ErrorWrapper errors={errors} fieldName='shopCode' />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label id='areaCode'>State</Form.Label>
                <Controller
                  as={Form.Select}
                  name="areaCode"
                  id="areaCode"
                  control={control}
                  placeholder="Select State"
                  options={areaCodeOptions}
                  onChange={([selectedOption]) => {
                    const shopCode = getValues('shopCode');
                    if (shopCode && shopCode.length) getTradeName(shopCode, selectedOption.value);
                    return selectedOption;
                  }}
                  isDisabled={!editable}
                  isSearchable={true}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label id='tradeName'>Trade Name</Form.Label>
                <Controller
                  as={CreatableSelect}
                  name="tradeName"
                  id="tradeName"
                  control={control}
                  rules={{ required: 'This Field can not be empty' }}
                  placeholder="Select or Enter Trade Name"
                  options={tradeNameOptions}
                  onCreateOption={v => {
                    let createdOption = { label: v, value: v };
                    if (brandNameRegex(v)) {
                      setError('tradeName', 'regex', brandNameRegex(v))
                    } else {
                      setValue("tradeName", createdOption, { shouldValidate: true });
                      setTradeNameOptions([...tradeNameOptions, createdOption,])
                    }
                  }}
                  isDisabled={!editable}
                />
                <ErrorWrapper errors={errors} fieldName='tradeName' />
              </Form.Group>
            </Col>
          </>
        )}
      </Row>
      {
        editable && (
          <FormFooter
            tooltipProps={why}
            btnProps={{
              label: 'Proceed',
              type: "submit",
              onClick: handleSubmit(onSubmit),
            }}
          />
        )}
    </form>
  );
};

BusinessDetails.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  fetchBusinessDetails: PropTypes.func,
  submitBusinessDetails: PropTypes.func,
};

BusinessDetails.defaultProps = {
  categories: [],
  fetchBusinessDetails: () => { },
  submitBusinessDetails: () => { },
  getAreaCodes: () => {},
};

const mapDispatchToProps = (dispatch) => ({
  fetchBusinessDetails: () => dispatch(businessDetailsActions.fetch()),
  submitBusinessDetails: (businessDetails) =>
    dispatch(businessDetailsActions.submitBusinessDetails(businessDetails)),
  fetchGstDetails: (gstin) =>
    dispatch(businessDetailsActions.fetchGstDetails(gstin)),
  fetchShopDetails: (regNo, areaCode) =>
    dispatch(businessDetailsActions.fetchShopDetails(regNo, areaCode)),
  getAreaCodes: () => dispatch(masterDataActions.getAreaCodes()),
});

const mapStateToProps = (state) => ({
  categories: state.onboardingApp.businessDetails.response,
  verifiedPanDetails: panDetailsSelectors.verifiedPanDetails(state),
  getMerchantBusinessDetails:
    merchantSelectors.getMerchantBusinessDetails(state),
  businessEntities: masterDataSelectors.businessEntities(state),
  areaCodes: masterDataSelectors.areaCodes(state),
  getTradeOptions: businessDetailsSelectors.getTradeOptions(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(BusinessDetails);
